import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReturnButton from '../../components/ui/ReturnButton'; 
import CardItem from './sections/CardItem';
import Header from '../../components/layout/Header/Header';
import Footer from '../../components/layout/Footer/Footer';
import { useParams } from 'react-router-dom';

const LIKED_ITEMS_KEY = 'liked_items';

const CategoryItemsPage = () => {
  const { id, type } = useParams(); 

  const [category, setCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likedItems, setLikedItems] = useState({});
  const [isLikesLoaded, setIsLikesLoaded] = useState(false); // Лайки загружены?
  const [rawItems, setRawItems] = useState([]); // Храним сырые данные с сервера
  
  const scrollPositionRef = useRef(0);

  // 1. ЗАГРУЗКА ЛАЙКОВ ИЗ LOCALSTORAGE (выполняется первой)
  useEffect(() => {
    const savedLikes = localStorage.getItem(LIKED_ITEMS_KEY);
    console.log('Читаем localStorage, savedLikes:', savedLikes);
    
    if (savedLikes) {
      try {
        const parsedLikes = JSON.parse(savedLikes);
        setLikedItems(parsedLikes);
        console.log('Загружены лайки из localStorage:', parsedLikes);
      } catch (e) {
        console.error('Ошибка при загрузке лайков:', e);
      }
    } else {
      console.log('В localStorage нет сохранённых лайков');
    }
    setIsLikesLoaded(true); // Говорим, что лайки загружены (даже если пустые)
  }, []);

  // 2. СОХРАНЕНИЕ ЛАЙКОВ (когда они меняются)
  useEffect(() => {
    if (isLikesLoaded) {
      localStorage.setItem(LIKED_ITEMS_KEY, JSON.stringify(likedItems));
      console.log('Сохранены лайки в localStorage:', likedItems);
    }
  }, [likedItems, isLikesLoaded]);

  // 3. ЗАГРУЗКА ДАННЫХ С СЕРВЕРА
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const apiUrl = process.env.REACT_APP_API_URL;
        
        const itemsEndpoint = type === 'locations' 
          ? `/categories/${id}/locations` 
          : `/categories/${id}/routes`;

        const [categoryResponse, itemsResponse] = await Promise.all([
          fetch(`${apiUrl}/categories/${id}`),
          fetch(`${apiUrl}${itemsEndpoint}`)
        ]);

        if (!categoryResponse.ok) {
          throw new Error(`Ошибка загрузки категории: ${categoryResponse.status}`);
        }
        if (!itemsResponse.ok) {
          throw new Error(`Ошибка загрузки элементов: ${itemsResponse.status}`);
        }

        const categoryData = await categoryResponse.json();
        const itemsData = await itemsResponse.json();
        
        const normalizedCategory = categoryData.data || categoryData;
        const normalizedItems = itemsData.data || (Array.isArray(itemsData) ? itemsData : []);
        
        setCategory(normalizedCategory);
        setRawItems(normalizedItems); // Сохраняем сырые данные

      } catch (err) {
        console.error('Ошибка:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (id && type) {
      scrollPositionRef.current = window.scrollY;
      fetchData();
    }
  }, [id, type]);

  // 4. ПРИМЕНЯЕМ СОРТИРОВКУ ТОЛЬКО КОГДА ЗАГРУЖЕНЫ И ЛАЙКИ, И ДАННЫЕ
  useEffect(() => {
    if (isLikesLoaded && rawItems.length > 0) {
      console.log('Применяем сортировку с лайками:', likedItems);
      console.log('Сырые данные:', rawItems);
      
      // Сортируем элементы: лайкнутые в начало
      const sortedItems = [...rawItems].sort((a, b) => {
        const aIsLiked = likedItems[a.id] === true;
        const bIsLiked = likedItems[b.id] === true;
        
        if (aIsLiked && !bIsLiked) return -1;
        if (!aIsLiked && bIsLiked) return 1;
        return 0;
      });
      
      setItems(sortedItems);
      
      // Восстанавливаем позицию скролла
      if (scrollPositionRef.current > 0) {
        setTimeout(() => {
          window.scrollTo(0, scrollPositionRef.current);
          scrollPositionRef.current = 0;
        }, 0);
      }
    }
  }, [isLikesLoaded, rawItems, likedItems]);

  // 5. ОБНОВЛЕНИЕ ПРИ ИЗМЕНЕНИИ ЛАЙКОВ (без перезагрузки страницы)
  useEffect(() => {
    if (isLikesLoaded && rawItems.length > 0 && items.length > 0) {
      const currentScroll = window.scrollY;
      
      const sortedItems = [...rawItems].sort((a, b) => {
        const aIsLiked = likedItems[a.id] === true;
        const bIsLiked = likedItems[b.id] === true;
        
        if (aIsLiked && !bIsLiked) return -1;
        if (!aIsLiked && bIsLiked) return 1;
        return 0;
      });
      
      setItems(sortedItems);
      
      setTimeout(() => {
        window.scrollTo(0, currentScroll);
      }, 0);
    }
  }, [likedItems, rawItems, isLikesLoaded, items.length]);

  // Обработчик переключения лайка
  const handleToggleLike = useCallback((itemId, isLiked) => {
    setLikedItems(prev => {
      const newLikedItems = { ...prev, [itemId]: isLiked };
      if (!isLiked) {
        delete newLikedItems[itemId];
      }
      console.log('Обновляем лайки, новое состояние:', newLikedItems);
      return newLikedItems;
    });
  }, []);

  // Показываем загрузку, пока не загрузились лайки И данные
  if (loading || !isLikesLoaded) return <div className="loading-spinner">Загрузка...</div>;
  if (error) return <div className="error-message">Ошибка: {error}</div>;
  if (!category) return <div>Категория с ID {id} не найдена</div>;
  
  return (
    <div id="tagpageContainer" className="locations-page">
      <Header />
      <div className="locations-content">
        <ReturnButton />
        
        <div className="locations-content">
          <h1 className="tagPageName">{category.name}</h1>
            {category.description && (
              <div className="tagPageDesc">
                {category.description.split('\\n').map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            )}
          
           <div className="tagPageDescStick">
             <svg>...</svg>
           </div>
        </div>
         
        <div id="cardItemWrapper">
          {items.length === 0 ? (
            <div className="no-items-message">Нет элементов в этой категории</div>
          ) : (
            items.map((item) => (
              <CardItem 
                key={item.id}
                id={item.id}
                name={item.name}
                isLiked={likedItems[item.id] === true}
                image={item.main_photo_url}
                // textBg='/elements/tagCultureLocs/c2.svg'
                link={`/${type === 'locations' ? 'locations' : 'routes'}/${item.id}`}
                onToggleLike={handleToggleLike}
              />
            ))
          )}
        </div>
      
        <Footer />
      </div>
    </div>
  );
};

export default CategoryItemsPage;